import {Injectable} from '@angular/core';
import {of, Observable, throwError} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';

import { ClientBankAccountReqRes, ClientBankAccountResponseBasic } from 'src/app/dashboard/models/bankAccount.model';
import { HttpService } from '../http.service';
import { AbstractRestService } from 'src/app/shared/types/AbstractRestService';
import { SearchOptionsRequest } from 'src/app/shared/models/SearchOptionsRequest.model';
import { SearchOptionsResponse } from 'src/app/shared/models/SearchOptionsResponse.model';
import { KeyValuePair } from 'src/app/shared/types/KeyValuePair.model';
import { ClientBankAccountsImportResponse } from 'src/app/shared/models/ClientBankAccountsImportResponse.model';
import { UnitBankAccountReqRes } from '../../shared/models/gp/UnitBankAccountReqRes.model';
import { MergeUnitClientBankAccountDto } from 'src/app/shared/models/MergeUnitClientBankAccountDto.model';

@Injectable({
  providedIn: 'root'
})
export class GpBankAccountDataService extends AbstractRestService<ClientBankAccountReqRes, ClientBankAccountResponseBasic> {
  protected readonly baseEndpoint$ = of('bankAccounts/');

  protected readonly byTargetCurrencyEndpoint$ = this.baseEndpoint$.pipe(map(endpoint => endpoint + 'targetCurrency/'));

  constructor(http: HttpService) {
    super(http);
  }

  public getListBasic(options: SearchOptionsRequest) {
    return super.getList(options);
  }

  public getListByTargetCurrency(currencyId: number, holdingId: number = null): Observable<ClientBankAccountResponseBasic[]> {
    return this.byTargetCurrencyEndpoint$.pipe(
      switchMap(endpoint => this.http.getTyped<ClientBankAccountResponseBasic[]>(`${endpoint}${currencyId}/${holdingId || ''}`)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public getInvestorBankAccount(bankAccount: MergeUnitClientBankAccountDto): Observable<MergeUnitClientBankAccountDto> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint => this.http.postTyped<MergeUnitClientBankAccountDto>(`${endpoint}holdings/investor-bank-account`, bankAccount)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public getListByHolding(holdingId: number): Observable<KeyValuePair<number, MergeUnitClientBankAccountDto[]>> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint => this.http.getTyped<KeyValuePair<number, MergeUnitClientBankAccountDto[]>>(`${endpoint}holdings/${holdingId}`)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public getInvestorClientListByHolding(holdingId: number): Observable<KeyValuePair<number, ClientBankAccountReqRes[]>> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint => this.http.getTyped<KeyValuePair<number, ClientBankAccountReqRes[]>>(`${endpoint}holdings/${holdingId}/investor-client-account`)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public getInvestorUnitAccountListByHolding(holdingId: number): Observable<KeyValuePair<number, UnitBankAccountReqRes[]>> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint => this.http.getTyped<KeyValuePair<number, UnitBankAccountReqRes[]>>(`${endpoint}holdings/${holdingId}/investor-unit-account`)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public getCreBanksListByHolding(holdingId: number): Observable<UnitBankAccountReqRes[]> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint => this.http.getTyped<UnitBankAccountReqRes[]>(`${endpoint}cre-bank-accounts/${holdingId}`)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public getOrganizationTypeCreBankAccounts(): Observable<UnitBankAccountReqRes[]> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint => this.http.getTyped<UnitBankAccountReqRes[]>(`${endpoint}cre-bank-accounts/organization/distribution`)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public getDistributionCreBankAccounts(holdingId: number): Observable<UnitBankAccountReqRes[]> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint => this.http.getTyped<UnitBankAccountReqRes[]>(`${endpoint}cre-bank-accounts/${holdingId}/distribution`)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public setPreferredCurrency(clientBankAccountId: number, currencyId: number) {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint =>
        this.http.putTyped<void>(`${endpoint}${clientBankAccountId}/set-preferred-currency`, {preferredCurrencyId: currencyId} as ClientBankAccountReqRes)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public getInvestorsList(searchOptions: SearchOptionsRequest): Observable<SearchOptionsResponse<ClientBankAccountReqRes>> {
    const queryParams = this.getSearchOptionsToQuerystring(searchOptions);
    return this.baseEndpoint$.pipe(
      switchMap(endpoint => this.http.getTyped<SearchOptionsResponse<ClientBankAccountReqRes>>(`${endpoint}investors` + queryParams)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public getInvestorsBankAccountsTemplate(): Observable<string> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint => this.http.getTyped<string>(`${endpoint}investors/template`)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public createInvestorsBankAccountsFromExcelFile(metaFileLinkId: number): Observable<ClientBankAccountsImportResponse> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint =>
        this.http.postTyped<ClientBankAccountsImportResponse>(`${endpoint}investors/import/${metaFileLinkId}`, null)
      ),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public deleteInvestorBankAccount(bankAccountId: number): Observable<void> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint =>
        this.http.deleteTyped<void>(`${endpoint}investors/${bankAccountId}`)
      ),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public isLastUnitBank(bankAccounts: ClientBankAccountResponseBasic[], i: number) {
    return bankAccounts[i].isUnitBankAccount && !bankAccounts[i + 1]?.isUnitBankAccount;
  }
}
